import type { WorkBook } from 'xlsx';
import { buildWorkbookFromAoA, downloadExcelWorkbook } from './exportExcel';
import {
  filterLeaveReportEntries,
  prepareLeaveReportExportRows,
  type LeaveReportEntryFilters,
  type LeaveReportFilterableEntry,
} from './leaveReportDateRange';

export type OrganisationLeaveDateRangeReportEntry = LeaveReportFilterableEntry & {
  employeeNumber?: number;
};

const REPORT_COLUMNS = [
  'Employee',
  'Employee Number',
  'Business Unit',
  'Department',
  'Leave Type',
  'From Date',
  'To Date',
  'Days Taken',
] as const;

const SHEET_NAME = 'Leave report';

export function getOrganisationLeaveDateRangeReportEntries({
  entries,
  ...filters
}: {
  entries: OrganisationLeaveDateRangeReportEntry[];
} & LeaveReportEntryFilters): OrganisationLeaveDateRangeReportEntry[] {
  const withDays = entries.filter((e) => e.daysTaken > 0);
  return prepareLeaveReportExportRows(filterLeaveReportEntries(withDays, filters));
}

/** Build an Excel workbook (one sheet) from filtered leave rows. */
export function buildOrganisationLeaveDateRangeReportWorkbook(
  rows: OrganisationLeaveDateRangeReportEntry[]
): WorkBook {
  const headerRow: string[] = [...REPORT_COLUMNS];
  const dataRows: (string | number)[][] = rows.map((row) => [
    row.employeeName,
    row.employeeNumber ?? '',
    row.businessUnitName ?? '',
    row.departmentName ?? '',
    row.leaveTypeName,
    row.fromDate?.slice(0, 10) ?? '',
    row.toDate?.slice(0, 10) ?? '',
    row.daysTaken,
  ]);
  const aoa: (string | number)[][] = [headerRow, ...dataRows];
  return buildWorkbookFromAoA(SHEET_NAME, aoa, [28, 16, 22, 22, 22, 12, 12, 12]);
}

/** Triggers a browser download of an `.xlsx` file with the same columns as the on-screen report. */
export function downloadOrganisationLeaveDateRangeReportExcel(
  rows: OrganisationLeaveDateRangeReportEntry[],
  dateFrom: string,
  dateTo: string
): void {
  const wb = buildOrganisationLeaveDateRangeReportWorkbook(rows);
  downloadExcelWorkbook(wb, buildOrganisationLeaveDateRangeReportFilename(dateFrom, dateTo));
}

export function buildOrganisationLeaveDateRangeReportFilename(
  dateFrom: string,
  dateTo: string
): string {
  const fromPart = (dateFrom || 'start').replace(/-/g, '');
  const toPart = (dateTo || 'end').replace(/-/g, '');
  return `organisation-leave-report-${fromPart}-${toPart}.xlsx`;
}
